// 🚀 최적화된 Storage Utils

// 1. 이미지 캐싱 시스템 추가
const imageCache = new Map();
const MAX_CACHE_SIZE = 10;
const CACHE_EXPIRY = 30 * 60 * 1000; // 30분

// 캐시 관리 함수
const getCachedImage = (url) => {
  const cached = imageCache.get(url);
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.file;
  }
  if (cached) {
    imageCache.delete(url);
  }
  return null;
};

const setCachedImage = (url, file) => {
  // 캐시 크기 제한
  if (imageCache.size >= MAX_CACHE_SIZE) {
    const firstKey = imageCache.keys().next().value;
    imageCache.delete(firstKey);
  }
  
  imageCache.set(url, {
    file,
    timestamp: Date.now()
  });
};

// 2. 최적화된 URL to File 변환
const createFileFromUrl = async (url, filename = 'image.jpg') => {
  if (!url) return null;
  
  // 캐시 확인
  const cachedFile = getCachedImage(url);
  if (cachedFile) {
    return cachedFile;
  }
  
  try {
    // 🎯 AbortController로 타임아웃 설정
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃
    
    const response = await fetch(url, { 
      signal: controller.signal,
      // 🚀 캐시 헤더 활용
      cache: 'force-cache'
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) return null;
    
    const blob = await response.blob();
    
    // 🎯 최소 파일 크기 검증
    if (blob.size < 100) return null;
    
    const file = new File([blob], filename, { type: blob.type });
    
    // 캐시에 저장
    setCachedImage(url, file);
    
    return file;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('Image load timeout:', url);
    }
    return null;
  }
};

// 3. 배치 처리 최적화
const loadMultipleImagesOptimized = async (imageUrls, options = {}) => {
  const { 
    maxConcurrency = 3, // 동시 요청 수 제한
    retryCount = 2,
    onProgress = null 
  } = options;
  
  const results = {};
  const queue = [...imageUrls];
  const inProgress = new Set();
  let completed = 0;
  
  const processNext = async () => {
    if (queue.length === 0 || inProgress.size >= maxConcurrency) {
      return;
    }
    
    const { key, url, filename } = queue.shift();
    inProgress.add(key);
    
    try {
      let attempt = 0;
      let file = null;
      
      while (attempt <= retryCount && !file) {
        file = await createFileFromUrl(url, filename);
        attempt++;
        
        if (!file && attempt <= retryCount) {
          // 지수 백오프로 재시도
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 100));
        }
      }
      
      if (file) {
        results[key] = file;
      }
      
    } catch (error) {
      console.warn(`Failed to load image ${key}:`, error);
    } finally {
      inProgress.delete(key);
      completed++;
      
      if (onProgress) {
        onProgress(completed, imageUrls.length);
      }
      
      // 다음 이미지 처리
      processNext();
    }
  };
  
  // 초기 동시 요청 시작
  const initialRequests = Math.min(maxConcurrency, queue.length);
  for (let i = 0; i < initialRequests; i++) {
    processNext();
  }
  
  // 모든 요청 완료 대기
  while (inProgress.size > 0 || queue.length > 0) {
    await new Promise(resolve => setTimeout(resolve, 50));
  }
  
  return results;
};

// 4. 최적화된 AI 결과 저장
export const saveAiResultsToStorage = async (results, userId, sessionId, selectedImageType, selectedBackgroundColor = '#ffffff') => {
  try {
    const getStorageKey = (suffix) => `ai_results_${userId || 'guest'}_${sessionId || 'temp'}_${suffix}`;
    
    // 🚀 원본 이미지를 WebP로 압축하여 저장 (용량 절약)
    const saveOriginalImageOptimized = async () => {
      if (results.originalFile) {
        try {
          // Canvas를 이용한 WebP 압축
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          return new Promise((resolve) => {
            img.onload = () => {
              // 최대 크기 제한 (512px)
              const maxSize = 512;
              const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
              
              canvas.width = img.width * scale;
              canvas.height = img.height * scale;
              
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              
              // WebP로 압축 (품질 80%)
              const webpData = canvas.toDataURL('image/webp', 0.8);
              resolve(webpData);
            };
            
            img.onerror = () => resolve(null);
            img.src = URL.createObjectURL(results.originalFile);
          });
        } catch (error) {
          return null;
        }
      }
      return null;
    };
    
    const originalBase64 = await saveOriginalImageOptimized();
    
    const storageData = {
      originalImageBase64: originalBase64,
      gfpganUrl: results.gfpganUrl,
      gfpganBackgroundRemoverUrl: results.gfpganBackgroundRemoverUrl,
      finalUrl: results.finalUrl,
      selectedImageType,
      selectedBackgroundColor,
      timestamp: Date.now(),
      version: '2.0' // 버전 정보 추가
    };
    
    // 🎯 용량 확인 후 저장
    const dataSize = JSON.stringify(storageData).length;
    if (dataSize > 5 * 1024 * 1024) { // 5MB 초과시
      // 원본 이미지 제외하고 저장
      delete storageData.originalImageBase64;
    }
    
    localStorage.setItem(getStorageKey('urls'), JSON.stringify(storageData));
  } catch (error) {
    // 저장 실패 시 조용히 무시
    console.warn('Failed to save AI results to storage:', error);
  }
};

// 5. 최적화된 AI 결과 복원
export const loadAiResultsFromStorage = async (userId, sessionId) => {
  try {
    const getStorageKey = (suffix) => `ai_results_${userId || 'guest'}_${sessionId || 'temp'}_${suffix}`;
    const storedData = localStorage.getItem(getStorageKey('urls'));
    if (!storedData) return null;

    const parsed = JSON.parse(storedData);
    
    // 24시간 이내 데이터만 복원
    if (Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(getStorageKey('urls'));
      return null;
    }

    // 🚀 배치 처리로 모든 이미지 동시 로드
    const imageUrls = [
      { key: 'gfpganFile', url: parsed.gfpganUrl, filename: `gfpgan-restored-${Date.now()}.jpg` },
      { key: 'gfpganBackgroundRemoverFile', url: parsed.gfpganBackgroundRemoverUrl, filename: `gfpgan-bg-remover-restored-${Date.now()}.jpg` },
      { key: 'finalFile', url: parsed.finalUrl, filename: `final-restored-${Date.now()}.jpg` }
    ].filter(item => item.url);
    
    // 원본 이미지 복원
    let originalFile = null;
    if (parsed.originalImageBase64) {
      try {
        const response = await fetch(parsed.originalImageBase64);
        const blob = await response.blob();
        originalFile = new File([blob], `original-restored-${Date.now()}.jpg`, { type: 'image/jpeg' });
      } catch (error) {
        // 원본 복원 실패는 조용히 무시
      }
    }
    
    // 🚀 진행률 콜백 포함한 배치 로딩
    const results = await loadMultipleImagesOptimized(imageUrls, {
      maxConcurrency: 2, // 동시 요청 수 제한
    });
    
    return {
      originalFile,
      ...results,
      gfpganBackgroundRemoverBlackWhiteFile: null, // 나중에 생성
      gfpganUrl: parsed.gfpganUrl,
      gfpganBackgroundRemoverUrl: parsed.gfpganBackgroundRemoverUrl,
      finalUrl: parsed.finalUrl,
      selectedImageType: parsed.selectedImageType || 'original',
      selectedBackgroundColor: parsed.selectedBackgroundColor || '#ffffff'
    };
  } catch (error) {
    console.warn('Failed to load AI results from storage:', error);
    return null;
  }
};

// 6. 메모리 정리 함수
export const cleanupImageCache = () => {
  imageCache.clear();
};

// 7. 저장된 결과 정리 (기존과 동일)
export const clearStoredResults = (userId, sessionId) => {
  try {
    const getStorageKey = (suffix) => `ai_results_${userId || 'guest'}_${sessionId || 'temp'}_${suffix}`;
    localStorage.removeItem(getStorageKey('urls'));
    
    // 캐시도 함께 정리
    cleanupImageCache();
  } catch (error) {
    console.warn('Failed to clear stored results:', error);
  }
};
